import { TitleCasePipe } from '@angular/common';
import {
    Component,
    Input,
    input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { UserDto } from '@zopup/zop-types';
import { AvatarModule } from 'primeng/avatar';
import { PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
    selector: 'zop-user-avatar',
    templateUrl: './user-avatar.component.html',
    styleUrls: ['./user-avatar.component.scss'],
    imports: [AvatarModule, PopoverModule, TitleCasePipe, SkeletonModule],
})
export class UserAvatarComponent implements OnInit, OnChanges {
    @Input() size: 'large' | 'xlarge' | 'normal' = 'normal';
    @Input() shape: 'circle' | 'square' = 'circle';
    @Input() user?: UserDto | null;
    @Input() loading: boolean = false;

    backgroundColor: string = '#000000';
    foregroundColor: string = '#FFFFFF';

    // Curated color palette with better visual appeal and accessibility
    private readonly colorPalette = [
        { bg: '#FF6B6B', fg: '#FFFFFF' }, // Coral Red
        { bg: '#4ECDC4', fg: '#000000' }, // Turquoise
        { bg: '#45B7D1', fg: '#000000' }, // Sky Blue
        { bg: '#FFA07A', fg: '#000000' }, // Light Salmon
        { bg: '#98D8C8', fg: '#000000' }, // Mint Green
        { bg: '#F7B731', fg: '#000000' }, // Sunflower Yellow
        { bg: '#5F27CD', fg: '#FFFFFF' }, // Purple
        { bg: '#00D2D3', fg: '#000000' }, // Cyan
        { bg: '#FF9FF3', fg: '#000000' }, // Pink
        { bg: '#54A0FF', fg: '#FFFFFF' }, // Blue
        { bg: '#48DBfB', fg: '#000000' }, // Light Blue
        { bg: '#1DD1A1', fg: '#000000' }, // Emerald
        { bg: '#10AC84', fg: '#FFFFFF' }, // Green
        { bg: '#EE5A6F', fg: '#FFFFFF' }, // Red
        { bg: '#C44569', fg: '#FFFFFF' }, // Dark Pink
        { bg: '#FD79A8', fg: '#000000' }, // Rose
        { bg: '#A29BFE', fg: '#000000' }, // Lavender
        { bg: '#6C5CE7', fg: '#FFFFFF' }, // Indigo
        { bg: '#FD7272', fg: '#FFFFFF' }, // Peach
        { bg: '#26DE81', fg: '#000000' }, // Light Green
    ];

    constructor() {}
    ngOnChanges(changes: SimpleChanges): void {
        this.backgroundColor = this.getUserColor();
        this.foregroundColor = this.getFontColor(this.backgroundColor);
    }

    ngOnInit() {}

        /**
     * Returns a color from the curated palette based on the user's name.
     * Uses consistent hashing to ensure the same name always gets the same color.
     * @returns A color from the predefined palette.
     */
    getUserColor(): string {
        if (this.user == null) return '#CCCCCC';
        const name: string = this.user.displayName || '';
        
        // Generate hash from name
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Map hash to color palette index
        const colorIndex = Math.abs(hash) % this.colorPalette.length;
        return this.colorPalette[colorIndex].bg;
    }

    /**
     * Returns the appropriate foreground color for the given background color.
     * Uses the curated color palette for consistent pairing.
     */
    getFontColor(backgroundColor: string): string {
        // Find the color in our palette
        const colorPair = this.colorPalette.find(
            (color) => color.bg === backgroundColor
        );
        
        if (colorPair) {
            return colorPair.fg;
        }

        // Fallback to contrast calculation for custom colors
        const rgb = this.hexToRgb(backgroundColor);
        if (!rgb) return '#FFFFFF';

        // Calculate brightness using the YIQ formula
        const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        return brightness > 128 ? '#000000' : '#FFFFFF';
    }

    /**
     * Convert HEX color to RGB
     */
    private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
        const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
        return match
            ? {
                  r: parseInt(match[1], 16),
                  g: parseInt(match[2], 16),
                  b: parseInt(match[3], 16),
              }
            : null;
    }

    /**
     * Get the size in pixels for the skeleton loader
     */
    getSkeletonSize(): string {
        switch (this.size) {
            case 'large':
                return '3rem';
            case 'xlarge':
                return '4rem';
            default:
                return '2rem';
        }
    }
}
